import { useEffect, useRef, useState } from 'react';

interface WhyChooseItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  iconBg?: string;
  color?: string;
  image?: string;
  link?: string;
}

interface WhyChooseUsProps {
  items?: WhyChooseItem[];
  loading?: boolean;
}

export default function WhyChooseUs({ items = [], loading = false }: WhyChooseUsProps) {
  const [isVisible, setIsVisible] = useState(false);
  const reasons = items; // Sử dụng items từ props
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const badges = [
    { name: 'ISO 27001', icon: 'ri-shield-check-line' },
    { name: 'OWASP', icon: 'ri-bug-line' },
    { name: 'GDPR', icon: 'ri-file-shield-line' }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-block px-4 py-2 bg-[#06b6d4]/10 rounded-full mb-6">
            <span className="text-[#06b6d4] text-sm font-semibold uppercase tracking-wider">Đối tác tin cậy</span>
          </div>
          <h2 className="font-['Inter',sans-serif] text-4xl md:text-5xl font-bold text-[#1a202c] mb-6">
            Vì sao chọn Chúng tôi?
          </h2>
          <p className="text-lg text-[#4a5568] max-w-3xl mx-auto">
            Chúng tôi không chỉ là nhà cung cấp dịch vụ, mà là đối tác đồng hành trong hành trình bảo mật của bạn.
          </p>
        </div>

        {/* Reasons - Zig-zag Layout */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-16 h-16 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Đang tải...</p>
          </div>
        ) : reasons.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Chưa có nội dung</p>
          </div>
        ) : (
          <div className="space-y-24">
            {reasons.map((reason, index) => {
              const ContentWrapper = reason.link ? 'a' : 'div';
              return (
                <div 
                  key={reason.id}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Image */}
                  {reason.image && (
                    <div 
                      className={`${index % 2 === 1 ? 'lg:order-2' : ''} transition-all duration-1000 ${
                        isVisible ? 'opacity-100 translate-x-0' : `opacity-0 ${index % 2 === 0 ? '-translate-x-10' : 'translate-x-10'}`
                      }`}
                      style={{ transitionDelay: `${index * 0.2}s` }}
                    >
                      <div className="relative rounded-2xl overflow-hidden border-2 border-[#e2e8f0] bg-white shadow-lg group hover:shadow-2xl transition-all duration-500">
                        <img 
                          src={reason.image}
                          alt={reason.title}
                          className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700 max-h-[300px]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#06b6d4]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div 
                    className={`${index % 2 === 1 ? 'lg:order-1' : ''} transition-all duration-1000 delay-200 ${
                      isVisible ? 'opacity-100 translate-x-0' : `opacity-0 ${index % 2 === 0 ? 'translate-x-10' : '-translate-x-10'}`
                    }`}
                    style={{ transitionDelay: `${index * 0.2 + 0.2}s` }}
                  >
                    <ContentWrapper 
                      href={reason.link || undefined}
                      className={`group ${reason.link ? 'cursor-pointer' : ''}`}
                    >
                      <div className={`w-16 h-16 rounded-xl ${reason.iconBg || 'bg-gradient-to-br from-[#06b6d4]/10 to-[#0891b2]/10'} flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                        <i className={`${reason.icon} text-3xl ${reason.color || 'text-[#06b6d4]'}`}></i>
                      </div>
                      
                      <h3 className="font-['Inter',sans-serif] text-3xl font-bold text-[#1a202c] mb-4 group-hover:text-[#06b6d4] transition-colors duration-300">
                        {reason.title}
                      </h3>
                      
                      <p className="text-lg text-[#4a5568] leading-relaxed mb-6">
                        {reason.description}
                      </p>

                      {/* Decorative button - always show */}
                      <div className="flex items-center gap-2 text-[#2a4b7c] font-semibold">
                        <span>Tìm hiểu thêm</span>
                        <i className="ri-arrow-right-line text-xl"></i>
                      </div>
                    </ContentWrapper>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Trust Badges */}
        <div className={`mt-20 transition-all duration-1000 delay-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="bg-gradient-to-r from-[#f7fafc] to-white rounded-2xl p-10 border-2 border-[#e2e8f0]">
            <div className="text-center mb-8">
              <h3 className="font-['Inter',sans-serif] text-2xl font-bold text-[#1a202c] mb-2">
                Tuân thủ các chuẩn quốc tế
              </h3>
              <p className="text-[#4a5568]">
                Đảm bảo chất lượng dịch vụ theo các tiêu chuẩn bảo mật hàng đầu
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6">
              {badges.map((badge, index) => (
                <div 
                  key={index}
                  className="group flex items-center gap-3 px-6 py-4 bg-white rounded-xl border-2 border-[#e2e8f0] hover:border-[#2a4b7c] hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  style={{ 
                    animationDelay: `${index * 0.1}s`,
                    animation: isVisible ? 'fadeInUp 0.6s ease-out both' : 'none'
                  }}
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2a4b7c]/10 to-[#1e3a63]/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <i className={`${badge.icon} text-xl text-[#2a4b7c]`}></i>
                  </div>
                  <span className="font-bold text-[#1a202c] text-lg group-hover:text-[#2a4b7c] transition-colors">
                    {badge.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}